import React, { useCallback, useEffect, useState } from 'react';
import { Play, Plus, X } from 'lucide-react';
import { apiService } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';

interface Rule {
  key: string;
  name: string;
  description: string | null;
  issue_types: string[] | null;
  check_type: string;
  pattern: string | null;
  message: string;
  suggestion: string | null;
  active: boolean;
  sort_order: number;
}
interface Verb { verb: string; source: 'standard' | 'added' }

function RuleRow({ rule, onSaved }: { rule: Rule; onSaved: () => void }) {
  const { showToast } = useToast();
  const [message, setMessage] = useState(rule.message);
  const [suggestion, setSuggestion] = useState(rule.suggestion ?? '');
  const [pattern, setPattern] = useState(rule.pattern ?? '');
  const [saving, setSaving] = useState(false);
  const dirty = message !== rule.message || suggestion !== (rule.suggestion ?? '') || pattern !== (rule.pattern ?? '');

  const save = async (body: Partial<{ active: boolean; message: string; suggestion: string | null; pattern: string | null }>) => {
    setSaving(true);
    try {
      const r = await apiService.updateNamingRule(rule.key, body);
      if (r.success) { showToast('success', `Saved "${rule.name}"`); onSaved(); }
      else showToast('error', r.error || 'Could not save the rule');
    } catch (e: any) {
      showToast('error', e?.response?.data?.error || 'Could not save the rule');
    } finally {
      setSaving(false);
    }
  };

  return (
    <li className={`px-5 py-4 ${rule.active ? '' : 'opacity-60'}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-semibold text-gray-900">{rule.name}</span>
            <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[0.6875rem] font-medium text-gray-600">{rule.issue_types ? rule.issue_types.join(', ') : 'every type'}</span>
            <span className="rounded-full bg-gray-100 px-2 py-0.5 font-mono text-[0.6875rem] text-gray-600">{rule.check_type}</span>
          </div>
          {rule.description && <div className="mt-1 text-xs text-gray-500">{rule.description}</div>}
        </div>
        <label className="flex items-center gap-2 text-xs text-gray-600">
          <input type="checkbox" checked={rule.active} disabled={saving} onChange={(e) => void save({ active: e.target.checked })} className="rounded border-gray-300 text-blue-600 focus:ring-blue-200" />
          Active
        </label>
      </div>
      <div className="mt-3 grid gap-2 md:grid-cols-3">
        <label className="text-xs text-gray-500">
          Reason shown
          <input value={message} onChange={(e) => setMessage(e.target.value)} className="mt-1 w-full rounded-lg border border-gray-200 px-2.5 py-1.5 text-sm text-gray-900 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100" />
        </label>
        <label className="text-xs text-gray-500">
          Suggestion
          <input value={suggestion} onChange={(e) => setSuggestion(e.target.value)} className="mt-1 w-full rounded-lg border border-gray-200 px-2.5 py-1.5 text-sm text-gray-900 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100" />
        </label>
        <label className="text-xs text-gray-500">
          Pattern or threshold
          <input value={pattern} onChange={(e) => setPattern(e.target.value)} placeholder="regex or number" className="mt-1 w-full rounded-lg border border-gray-200 px-2.5 py-1.5 font-mono text-xs text-gray-900 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100" />
        </label>
      </div>
      {dirty && (
        <div className="mt-2 flex gap-2">
          <button type="button" disabled={saving} onClick={() => void save({ message, suggestion: suggestion || null, pattern: pattern || null })} className="rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-blue-700 active:scale-[0.98] disabled:opacity-50 motion-reduce:transition-none">Save</button>
          <button type="button" onClick={() => { setMessage(rule.message); setSuggestion(rule.suggestion ?? ''); setPattern(rule.pattern ?? ''); }} className="rounded-lg px-3 py-1.5 text-xs font-medium text-gray-600 transition-colors hover:bg-gray-100 motion-reduce:transition-none">Discard</button>
        </div>
      )}
    </li>
  );
}

// Admin editor for the naming engine: switch rules on or off, edit the wording,
// maintain the verb list, and re-evaluate every title after a change.
export default function NamingRulesTab() {
  const { user } = useAuth();
  const { showToast } = useToast();
  const [rules, setRules] = useState<Rule[]>([]);
  const [verbs, setVerbs] = useState<Verb[]>([]);
  const [newVerb, setNewVerb] = useState('');
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, v] = await Promise.all([apiService.getNamingRules(), apiService.getNamingVerbs()]);
      if (r.success) setRules(r.data ?? []);
      if (v.success) setVerbs(v.data ?? []);
    } catch (e: any) {
      showToast('error', e?.response?.data?.error || 'Could not load the naming rules');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => { if (user?.role === 'admin') void load(); }, [user, load]);

  const addVerb = async () => {
    const verb = newVerb.trim().toLowerCase();
    if (!verb) return;
    try {
      const r = await apiService.addNamingVerb(verb);
      if (r.success) { setNewVerb(''); void load(); showToast('success', `Added "${verb}"`); }
      else showToast('error', r.error || 'Could not add the verb');
    } catch (e: any) {
      showToast('error', e?.response?.data?.error || 'Could not add the verb');
    }
  };

  const removeVerb = async (verb: string) => {
    try {
      const r = await apiService.removeNamingVerb(verb);
      if (r.success) { void load(); showToast('success', `Removed "${verb}"`); }
    } catch (e: any) {
      showToast('error', e?.response?.data?.error || 'Could not remove the verb');
    }
  };

  const reevaluate = async () => {
    setRunning(true);
    try {
      const r = await apiService.runNamingEvaluate();
      if (r.success) showToast('success', 'Re-evaluation started. Progress shows under Update data.');
      else showToast('error', r.error || 'Could not start the re-evaluation');
    } catch (e: any) {
      showToast('error', e?.response?.data?.error || 'Could not start the re-evaluation');
    } finally {
      setRunning(false);
    }
  };

  if (user?.role !== 'admin') return null;

  return (
    <div className="space-y-6">
      <div className="overflow-hidden rounded-2xl bg-white shadow-sm ring-1 ring-gray-200/70">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 px-5 py-4">
          <div>
            <h3 className="text-sm font-semibold text-gray-900">Naming rules</h3>
            <p className="mt-0.5 text-xs text-gray-500">Each rule adds a reason and a suggestion to a title; a title with no findings is clear. Edits apply to new evaluations; re-evaluate to apply them everywhere.</p>
          </div>
          <button type="button" onClick={() => void reevaluate()} disabled={running} className="inline-flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-blue-700 active:scale-[0.98] disabled:opacity-50 motion-reduce:transition-none">
            <Play className="h-3.5 w-3.5" aria-hidden="true" />
            Re-evaluate all titles
          </button>
        </div>
        {loading ? (
          <div className="px-5 py-10 text-center text-sm text-gray-500">Loading…</div>
        ) : (
          <ul className="divide-y divide-gray-100">
            {rules.map((rule) => <RuleRow key={rule.key} rule={rule} onSaved={load} />)}
          </ul>
        )}
      </div>

      <div className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-gray-200/70">
        <h3 className="text-sm font-semibold text-gray-900">Action verbs</h3>
        <p className="mt-0.5 text-xs text-gray-500">A title that starts with one of these counts as saying what will be done. The twelve from the standard are marked; the rest were added because teams already use them in clear titles.</p>
        <div className="mt-3 flex items-center gap-2">
          <input value={newVerb} onChange={(e) => setNewVerb(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') void addVerb(); }} aria-label="Add a verb" placeholder="add a verb, e.g. calibrate" className="w-60 rounded-lg border border-gray-200 px-2.5 py-1.5 text-sm focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100" />
          <button type="button" onClick={() => void addVerb()} className="inline-flex items-center gap-1 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-50 active:scale-[0.98] motion-reduce:transition-none">
            <Plus className="h-3.5 w-3.5" aria-hidden="true" /> Add
          </button>
        </div>
        <div className="mt-3 flex flex-wrap gap-1.5">
          {verbs.map((v) => (
            <span key={v.verb} className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[0.6875rem] font-medium ring-1 ring-inset ${v.source === 'standard' ? 'bg-blue-50 text-blue-700 ring-blue-200' : 'bg-gray-50 text-gray-700 ring-gray-200'}`}>
              {v.verb}
              <button type="button" aria-label={`Remove ${v.verb}`} onClick={() => void removeVerb(v.verb)} className="-my-1 -mr-1 rounded-full p-1.5 text-gray-500 transition-colors hover:bg-white hover:text-gray-700 motion-reduce:transition-none">
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}
